import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import { supabase } from '@/lib/supabase';

// Define the structure for a single notification item
interface Notification {
  id: string;
  type: string;
  title: string;
  subtitle?: string;
  message: string;
  link?: string;
  time: string;
  isClickable: boolean;
  action?: string;
  icon: string;
  backgroundColor: string;
  textColor: string;
  isUnderlined: boolean;
  timestamp: number;
  isRead?: boolean; // This might be redundant if using notification_id and read_at from the backend
  banner?: string | null;
  oneSignalId?: string; // For OneSignal specific IDs
  rawData?: any;
}

// Define the structure for notification status from the backend/cache
interface UserNotificationStatus {
  id: string; // Unique ID for the user's notification record
  notification_id: string; // ID referencing the actual notification content
  is_read: boolean;
  read_at: string | null; // ISO 8601 timestamp or null
  notification: Notification; // The actual notification data
}

// Define the context type with new cache management methods
interface NotificationContextType {
  notifications: UserNotificationStatus[];
  unreadCount: number;
  loading: boolean;
  loadNotifications: (forceRefresh?: boolean) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  addNotification: (notification: UserNotificationStatus) => void;
  invalidateCache: () => Promise<void>;
  forceGlobalRefresh: () => Promise<void>;
}

// Create the context with a default undefined value
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);


// Provider component for managing notification state and logic
export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<UserNotificationStatus[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set()); // To track ongoing operations
  const [currentUserId, setCurrentUserId] = useState<string | null>(null); // To track current user for cache management

  // Load notifications with enhanced error handling and retry logic
  const loadNotifications = useCallback(async (forceRefresh = false) => {
    if (loading && !forceRefresh) return;

    try {
      setLoading(true);
      console.log('🔔 [NotificationContext] Loading notifications...');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('⚠️ [NotificationContext] No authenticated user found');
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      // Handle user context switch if needed
      if (currentUserId && currentUserId !== user.id) {
        console.log('🔄 [NotificationContext] User context switch detected');
        const { default: NotificationCacheService } = await import('../services/notificationCacheService');
        await NotificationCacheService.handleUserSwitch(user.id);
        setCurrentUserId(user.id);
      } else if (!currentUserId) {
        setCurrentUserId(user.id);
      }

      const { default: NotificationCacheService } = await import('../services/notificationCacheService');
      const result = await NotificationCacheService.getNotifications(user.id, forceRefresh);

      const userNotifications: UserNotificationStatus[] = result.notifications.map((cached: any) => ({
        id: cached.id,
        notification_id: cached.notification_id,
        is_read: cached.is_read,
        read_at: cached.read_at || null,
        notification: {
          id: cached.notification?.id || cached.notification_id,
          type: cached.notification?.type || 'general',
          title: cached.notification?.title || '',
          subtitle: cached.notification?.subtitle,
          message: cached.notification?.message || '',
          link: cached.notification?.link_url,
          time: cached.notification?.sent_at || cached.notification?.created_at || new Date().toISOString(),
          isClickable: !!cached.notification?.link_url,
          action: cached.notification?.action,
          icon: cached.notification?.icon || 'notifications',
          backgroundColor: cached.notification?.background_color || '#015480',
          textColor: cached.notification?.text_color || '#FFFFFF',
          isUnderlined: false,
          timestamp: new Date(cached.notification?.sent_at || cached.notification?.created_at || Date.now()).getTime(),
          isRead: cached.is_read,
          banner: cached.notification?.banner_url,
          rawData: cached.notification
        }
      }));

      setNotifications(userNotifications);
      setUnreadCount(result.unread_count);

      console.log('✅ [NotificationContext] Notifications loaded:', {
        count: userNotifications.length,
        unread: result.unread_count,
        fromCache: result.from_cache
      });

    } catch (error) {
      console.error('❌ [NotificationContext] Error loading notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [loading, currentUserId]);

  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    loadNotifications();
  }, []);

  useEffect(() => {
    if (currentUserId) {
      loadNotifications(true);
    }
  }, [currentUserId]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState: AppStateStatus) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        console.log('🔔 [NotificationContext] App returned to foreground, refreshing notifications');
        loadNotifications(true);
      }
      appStateRef.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [loadNotifications]);

  // Add a new notification (consider how this interacts with backend cache)
  const addNotification = (notification: UserNotificationStatus) => {
    // This function needs to be re-evaluated if notifications are primarily managed by cache service
    // For now, it might just update local state and rely on cache invalidation
    console.warn('⚠️ addNotification called - ensure this is coordinated with cache service');

    setNotifications(prev => {
      const existingIndex = prev.findIndex(n => n.id === notification.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = notification;
        return updated;
      }
      // Limit local notifications to prevent memory issues, rely on cache for history
      return [notification, ...prev].slice(0, 50);
    });
    setUnreadCount(prev => prev + (notification.is_read ? 0 : 1));
  };

  // Mark a specific notification as read
  const markAsRead = async (notificationId: string) => {
    if (processingIds.has(notificationId)) return; // Prevent concurrent operations

    setProcessingIds(prev => new Set(prev).add(notificationId));
    console.log('📱 [NotificationContext] Marking notification as read:', notificationId);

    try {
      const { default: NotificationCacheService } = await import('../services/notificationCacheService');
      await NotificationCacheService.markAsRead(notificationId);

      // Update local state optimistically
      setNotifications(prev => prev.map(n =>
        (n.id === notificationId || n.notification_id === notificationId) ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1)); // Decrement unread count optimistically

      // Decrement badge count when user taps a single notification
      try {
        const { default: BadgeManagerService } = await import('@/services/badgeManagerService');
        await BadgeManagerService.decrementBadge();
        console.log('📱 Badge decremented after marking notification as read');
      } catch (badgeError: any) {
        console.warn('⚠️ Badge decrement failed:', badgeError?.message);
      }

      console.log('✅ [NotificationContext] Notification marked as read:', notificationId);
    } catch (error) {
      console.error('❌ [NotificationContext] Error marking notification as read:', error);
      // Revert optimistic update on error
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
      loadNotifications(true); // Force refresh to sync state
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (loading) return;

    try {
      setLoading(true);
      console.log('📚 [NotificationContext] Marking all notifications as read...');

      const { default: NotificationCacheService } = await import('../services/notificationCacheService');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      await NotificationCacheService.markAllAsRead(user.id);

      // Update local state and unread count
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })));
      setUnreadCount(0);

      console.log('✅ [NotificationContext] All notifications marked as read.');

      // Attempt to clear app badge count
      try {
        const { default: BadgeService } = await import('@/services/badgeService');
        await BadgeService.clearBadge(); // Assuming BadgeService.clearBadge() exists and returns a promise
        console.log('📱 App badge cleared after marking all as read.');
      } catch (badgeError: any) {
        console.error('❌ Badge clear failed after marking all as read:', badgeError?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('❌ [NotificationContext] Error marking all notifications as read:', error);
      // Consider forcing a refresh if this fails to sync state
      loadNotifications(true);
    } finally {
      setLoading(false);
    }
  };


  // Invalidate cache when needed
  const invalidateCache = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { default: NotificationCacheService } = await import('../services/notificationCacheService');
        await NotificationCacheService.invalidateCache(user.id);
        console.log('🗑️ [NotificationContext] Cache invalidated manually for user:', user.id);
      }
    } catch (error) {
      console.error('❌ [NotificationContext] Error invalidating cache:', error);
    }
  }, []);

  // Force global cache refresh
  const forceGlobalRefresh = useCallback(async () => {
    try {
      const { default: NotificationCacheService } = await import('../services/notificationCacheService');
      await NotificationCacheService.forceGlobalRefresh();
      await loadNotifications(true); // Refresh local state after global refresh
      console.log('🔄 [NotificationContext] Global cache refresh completed and local state updated.');
    } catch (error) {
      console.error('❌ [NotificationContext] Error in global refresh:', error);
    }
  }, [loadNotifications]);

  // Provide context value to children
  const value = {
    notifications,
    unreadCount,
    loading,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    refreshNotifications: () => loadNotifications(true),
    addNotification,
    invalidateCache,
    forceGlobalRefresh
  };

  // Set global context reference for OneSignalService
  useEffect(() => {
    setGlobalNotificationContext(value);
    console.log('🔔 NotificationContext mounted and global reference set');
    
    return () => {
      setGlobalNotificationContext(null);
      console.log('🔔 NotificationContext unmounted and global reference cleared');
    };
  }, [value]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Custom hook to consume the Notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Global reference for OneSignalService to trigger refreshes
let globalNotificationContext: NotificationContextType | null = null;

export const setGlobalNotificationContext = (context: NotificationContextType | null) => {
  globalNotificationContext = context;
  console.log('🔔 Global notification context set:', context ? 'available' : 'cleared');
};

export const getGlobalNotificationContext = (): NotificationContextType | null => {
  return globalNotificationContext;
};

export const triggerGlobalNotificationRefresh = async (): Promise<void> => {
  if (globalNotificationContext?.refreshNotifications) {
    try {
      await globalNotificationContext.refreshNotifications();
      console.log('🔄 Global notification refresh triggered');
    } catch (error: any) {
      console.error('❌ Failed to trigger global notification refresh:', error?.message);
    }
  } else {
    console.warn('⚠️ Global notification context not available for refresh');
  }
};